import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import StarrySky from './StarrySky';
import CloudSky from './CloudSky';
import { FEELING_ICONS } from './FeelingChips';
import { FEELING_PAGES, FEELINGS_HUB } from '../content/feelingPages';
import { skyFeelingLabel, FEELING_COUNT_MIN } from '../content/skyFeelings';
import { API_BASE } from '../lib/config';
import WmhdCard from './WmhdCard';

// /sky-of-thoughts/feelings: every feeling with a short line people can relate
// to, and how many shared it. Each card opens the Sky gathered on that feeling.
export default function FeelingsHub({ isNight }: { isNight: boolean }) {
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    fetch(`${API_BASE}/api/feelings`).then(r => r.json()).then(d => setCounts(d.counts ?? {})).catch(() => setCounts({}));
  }, []);

  const people = (n: number) => (n >= FEELING_COUNT_MIN ? `${n} people shared this` : n > 0 ? 'A few people shared this' : 'Be one of the first');

  return (
    <div className="relative">
      <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
        {isNight ? <StarrySky className="absolute inset-0" /> : <CloudSky className="absolute inset-0" />}
        <div className={`absolute inset-0 ${isNight ? 'bg-black/30' : 'bg-white/20'}`} />
      </div>
      <div className={`max-w-4xl mx-auto px-4 relative z-10 pt-4 md:pt-8 pb-24 ${isNight ? 'text-white' : 'text-black'}`}>
        <p className="text-[0.625rem] uppercase tracking-[0.25em] opacity-60">Sky of Thoughts</p>
        <h1 className="mt-3 text-4xl md:text-6xl leading-[1.05] font-normal" style={{ fontFamily: "'Instrument Serif', serif", textWrap: 'balance' }}>
          {FEELINGS_HUB.heading} <em>{FEELINGS_HUB.headingEm}</em>
        </h1>
        <p className="mt-4 max-w-xl text-base md:text-lg opacity-80 leading-relaxed">{FEELINGS_HUB.lead}</p>

        <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FEELING_PAGES.map(p => {
            const Icon = FEELING_ICONS[p.key];
            return (
              <li key={p.key}>
                <Link
                  to={`/sky-of-thoughts/${p.slug}`}
                  className={`group h-full flex gap-4 items-start rounded-3xl p-5 border backdrop-blur-md transition-colors ${
                    isNight ? 'bg-white/[0.06] border-white/12 hover:bg-white/10' : 'bg-white/60 border-black/5 hover:bg-white/80'
                  }`}
                >
                  <span className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${isNight ? 'bg-white/10 text-amber-100' : 'bg-black/5 text-amber-800'}`}>
                    {Icon && <Icon size={18} strokeWidth={1.75} aria-hidden="true" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-2xl leading-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>{skyFeelingLabel(p.key)}</span>
                      <ArrowRight size={16} className="opacity-40 group-hover:opacity-90 transition-opacity" aria-hidden="true" />
                    </span>
                    <span className="block mt-1 text-sm opacity-80 italic" style={{ fontFamily: "'Instrument Serif', serif", fontSize: '1.05rem' }}>{p.line}</span>
                    <span className="block mt-2 text-xs opacity-55">{counts ? people(counts[p.key] ?? 0) : ' '}</span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/sky-of-thoughts" className={`px-6 py-3 rounded-full text-xs font-semibold tracking-widest uppercase ${isNight ? 'bg-white text-black' : 'bg-[#1a1a1a] text-white'}`}>
            See the full Sky
          </Link>
          <Link to="/let-go" className={`px-6 py-3 rounded-full text-xs font-semibold tracking-widest uppercase border ${isNight ? 'border-white/20' : 'border-black/15'}`}>
            Let it go privately
          </Link>
        </div>
        <WmhdCard isNight={isNight} seasonOnly className="mt-10" />
      </div>
    </div>
  );
}
